
const allEnv = Object.keys(process.env)
  .reduce((env, key) => {
    env[key] = process.env[key];
    return env;
  }, {} as Record<string, string | undefined>)

enum EnvList {
  dev = 'dev',
  development = 'dev',
  staging = 'staging',
  uat = 'uat',
  prod = 'prod',
  production = 'prod',
  /** Matches REACT_APP_ENV=preProduction (lowercased) so flags use *_PROD, not *_DEV */
  preproduction = 'prod',
}
const envList: Record<keyof typeof EnvList, string> = { ...EnvList }
const rawEnv = process.env.REACT_APP_ENV?.toLowerCase() ?? 'dev'

const Node_Env = envList[rawEnv as keyof typeof envList] ?? envList.dev
// Note: Old logic for environment
// const Node_Env = process.env.REACT_APP_ENV?.toLowerCase() === EnvList.dev ? '' : process.env.REACT_APP_ENV?.toLowerCase() === 'prod' ? 'PROD' : process.env.REACT_APP_ENV === 'Uat' ? 'UAT' : process.env.REACT_APP_ENV === 'Staging' ? 'Staging' : 'Development';

const apiList: Record<string, string> = {
    dev: 'http://localhost:8080/api',
    uat: 'https://filemule-internal-api-uat-c7cvhdarhxcqexgr.centralus-01.azurewebsites.net/api',
    staging: 'https://filemule-internal-api-staging-bqgvf9cdh5ard6hw.centralus-01.azurewebsites.net/api',
    prod: 'https://filemule-internal-api-c7cvhdarhxcqexgr.centralus-01.azurewebsites.net/api',
    // live: 'http://filemule-api-live.azurewebsites.net/api',
};

const API_URL = apiList[Node_Env];

const parseFlagValue = (value: unknown, fallback = false): boolean => {
    if (typeof value === 'boolean') return value;
    if (typeof value === 'number') return value !== 0;
    if (typeof value !== 'string') return fallback;

    const normalized = value.trim().toLowerCase();
    if (['true', '1', 'yes', 'y', 'on'].includes(normalized)) return true;
    if (['false', '0', 'no', 'n', 'off'].includes(normalized)) return false;
    return fallback;
};

/**
 * Runtime feature flags — populated from GET /config/feature-flags after login.
 * Defaults are false until the API responds; the API owns resolution (Azure App Config + server env fallback).
 */
export const AppFlags = {
    enableStateReports: false,
    enableSurplusLinesTaxEstimator: false,
    enableCreateDiligentSearchForm: false,
    /** Red extraction-exception outline on filing queues. OFF until Live go-live. */
    enableFilingReviewUi: false,
    /** Batch Filing Review nav / screens. Independent of the red-outline flag. */
    enableBatchFilingReview: false,
};

export type FeatureFlagsApiPayload = {
    enableStateReports?: unknown;
    enableSurplusLinesTaxEstimator?: unknown;
    enableCreateDiligentSearchForm?: unknown;
    enableFilingReviewUi?: unknown;
    enableBatchFilingReview?: unknown;
};

/** Apply flags from GET /config/feature-flags (single source of truth on the client). */
export const applyFeatureFlagsFromApi = (data: FeatureFlagsApiPayload | null | undefined) => {
    if (!data) return;

    if (data.enableStateReports !== undefined) {
        AppFlags.enableStateReports = parseFlagValue(data.enableStateReports, false);
    }
    if (data.enableSurplusLinesTaxEstimator !== undefined) {
        AppFlags.enableSurplusLinesTaxEstimator = parseFlagValue(
            data.enableSurplusLinesTaxEstimator,
            false,
        );
    }
    if (data.enableCreateDiligentSearchForm !== undefined) {
        AppFlags.enableCreateDiligentSearchForm = parseFlagValue(
            data.enableCreateDiligentSearchForm,
            false,
        );
    }
    if (data.enableFilingReviewUi !== undefined) {
        AppFlags.enableFilingReviewUi = parseFlagValue(data.enableFilingReviewUi, false);
    }
    if (data.enableBatchFilingReview !== undefined) {
        AppFlags.enableBatchFilingReview = parseFlagValue(data.enableBatchFilingReview, false);
    }
};

export const getStateReportsFeatureEnabled = () => AppFlags.enableStateReports;

export const getSurplusLinesTaxEstimatorFeatureEnabled = () => AppFlags.enableSurplusLinesTaxEstimator;

export const getCreateDiligentSearchFormFeatureEnabled = () =>
    AppFlags.enableCreateDiligentSearchForm;

export const getFilingReviewUiFeatureEnabled = () => AppFlags.enableFilingReviewUi;

export const getBatchFilingReviewFeatureEnabled = () => AppFlags.enableBatchFilingReview;

// Feature flags: configure on the API (Azure App Config + server env fallback). Client reads GET /config/feature-flags only.

const config = {
  allEnv,     // Note: all variables in .env
  Node_Env,   // Note: Current Environment.
  envList,    // Note: List of environments.
  url: {
    API_URL   // Note: API URL for the current enviroment.
  },
  AppFlags,   // Note: Feature Flag configuration for the current environment.
}

export default config
